import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { ProductCard } from '../components/ProductCard';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Renders [text](url) links and **bold** spans inside paragraph strings
const RichText = ({ text }) => {
    const parts = text.split(/(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*)/g);
    return (
        <>
            {parts.map((part, i) => {
                const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
                if (linkMatch) {
                    const [, label, url] = linkMatch;
                    return url.startsWith('/') ? (
                        <Link key={i} to={url} className="text-copper-600 hover:text-copper-700 underline">{label}</Link>
                    ) : (
                        <a key={i} href={url} className="text-copper-600 hover:text-copper-700 underline">{label}</a>
                    );
                }
                const boldMatch = part.match(/^\*\*([^*]+)\*\*$/);
                if (boldMatch) {
                    return <strong key={i} className="font-semibold text-nature-900">{boldMatch[1]}</strong>;
                }
                return part;
            })}
        </>
    );
};

export default function CategoryPage() {
    const { categorySlug } = useParams();
    const [category, setCategory] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            setCategory(null);
            try {
                const catRes = await axios.get(`${API}/category-pages/${categorySlug}`);
                setCategory(catRes.data);
            } catch (err) {
                setError(err.response?.status === 404 ? 'Category not found' : (err.message || 'Failed to load category'));
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [categorySlug]);

    if (loading) {
        return (
            <div className="min-h-screen bg-nature-50 pt-24">
                <div className="max-w-7xl mx-auto px-6 py-12">
                    <div className="h-4 w-48 bg-nature-100 rounded skeleton mx-auto mb-4" />
                    <div className="h-10 w-2/3 bg-nature-100 rounded skeleton mx-auto mb-10" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {[...Array(5)].map((_, i) => (
                            <div key={i} className="bg-nature-100 rounded-2xl aspect-[4/5] skeleton" />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (error || !category) {
        return (
            <div className="min-h-screen bg-nature-50 pt-24">
                <div className="max-w-7xl mx-auto px-6 py-16 text-center" data-testid="category-error">
                    <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <h1 className="font-heading text-2xl text-nature-900 mb-2">{error || 'Category not found'}</h1>
                    <p className="text-nature-800/60 font-body mb-6">This category page doesn't exist or couldn't be loaded.</p>
                    <Button asChild variant="outline" className="rounded-full">
                        <Link to="/recordings">Browse all recordings</Link>
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-nature-50 pt-24" data-testid="category-page">
            <SEO
                title={category.seo_title}
                description={category.seo_description}
                canonical={`https://hypnosismasteryseries.com/recordings/${category.slug}`}
            />

            {/* ItemList Schema.org Structured Data */}
            <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "ItemList",
                    "name": category.h1,
                    "description": category.seo_description,
                    "url": `https://hypnosismasteryseries.com/recordings/${category.slug}`,
                    "numberOfItems": (category.recordings || []).length,
                    "itemListElement": (category.recordings || []).map((rec, i) => ({
                        "@type": "ListItem",
                        "position": i + 1,
                        "url": `https://hypnosismasteryseries.com/recording/${rec.slug}`,
                        "name": rec.title
                    }))
                })}
            </script>
            <div className="max-w-7xl mx-auto px-6 py-12">
                {/* Header */}
                <div className="text-center mb-10">
                    <div className="w-10 h-1 rounded-full mx-auto mb-5 opacity-0 animate-fade-in-up" style={{ backgroundColor: category.accent || '#b0713f' }} data-testid="category-accent-bar" />
                    <p className="font-body text-sm font-medium tracking-wide uppercase mb-3 opacity-0 animate-fade-in-up" style={{ color: category.accent || '#b0713f' }} data-testid="category-volume-label">
                        {category.volume}
                    </p>
                    <h1 className="font-heading text-4xl sm:text-5xl font-light text-nature-900 mb-4 opacity-0 animate-fade-in-up" data-testid="category-h1">
                        {category.h1}
                    </h1>
                    <p className="text-nature-800/70 font-body max-w-3xl mx-auto opacity-0 animate-fade-in-up stagger-1" data-testid="category-subheading">
                        {category.subheading}
                    </p>
                </div>

                {/* Intro copy above the grid */}
                <div className="max-w-3xl mx-auto prose-flow mb-12 opacity-0 animate-fade-in-up stagger-1" data-testid="category-intro">
                    {(category.intro || []).map((para, i) => (
                        <p key={i} className="text-nature-800/70 font-body leading-relaxed"><RichText text={para} /></p>
                    ))}
                </div>

                {/* Recordings Grid */}
                <div className="max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-24" data-testid="category-recordings-grid">
                    {(category.recordings || []).map((recording, index) => (
                        <ProductCard key={recording.id} recording={recording} index={index} />
                    ))}
                </div>

                {/* Supporting SEO copy below the grid */}
                <div className="max-w-3xl mx-auto" data-testid="category-body-section">
                    {(category.sections || []).map((section, si) => (
                        <div key={si} className="category-section prose-flow">
                            <h2 className="font-heading text-lg text-nature-900 mb-5">{section.heading}</h2>
                            {(section.items || []).length > 0 && (
                                <ul className="mb-5">
                                    {section.items.map((item, ii) => (
                                        <li key={ii} className="text-nature-800/70 font-body leading-relaxed">
                                            <Link
                                                to={`/recording/${item.slug}`}
                                                className="font-semibold text-nature-900 hover:text-copper-600 transition-colors"
                                            >
                                                {item.title}
                                            </Link>
                                            {' — '}
                                            <RichText text={item.text} />
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {(section.paragraphs || []).map((para, pi) => (
                                <p key={pi} className="text-nature-800/70 font-body leading-relaxed"><RichText text={para} /></p>
                            ))}
                        </div>
                    ))}
                </div>

                {/* Explore another volume */}
                {category.next_category && (
                    <div className="max-w-3xl mx-auto mt-20 pt-10 border-t border-nature-200 text-center" data-testid="next-volume-link">
                        <p className="text-nature-800/50 font-body text-xs font-medium tracking-wide uppercase mb-3">
                            Explore another volume
                        </p>
                        <Link to={`/recordings/${category.next_category.slug}`} className="group inline-block" data-testid="next-volume-target">
                            <p className="font-body text-xs font-medium tracking-wide uppercase mb-1" style={{ color: category.next_category.accent }}>
                                {category.next_category.volume}
                            </p>
                            <span className="font-heading text-lg text-nature-900 group-hover:text-copper-600 transition-colors">
                                {category.next_category.card_title}
                                <span className="inline-block ml-2 transition-transform duration-200 group-hover:translate-x-1">→</span>
                            </span>
                        </Link>
                        <p className="mt-4">
                            <Link to="/recordings" className="text-sm font-body text-nature-800/60 hover:text-copper-600 underline transition-colors" data-testid="browse-all-link">
                                or browse all volumes
                            </Link>
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
